import { Fragment, type ReactNode } from "react";
import classNames from "classnames";
import { MediaQuery } from "@outplayed/responsive";
import { TooltipContainer } from "@outplayed/tooltips";
import { asArray, asText } from "@ugg/shared/utils/safe-coerce";
import { ReactComponent as Warning } from "@ugg/shared/assets/svg/warning.svg";
import { ReactComponent as ArrowRight } from "@ugg/shared/assets/svg/arrow-right.svg";
import {
  getClassicChampionSkillImgUrl,
  getClassicSpellIconUrl,
  getClassicRuneIconUrl,
  type ClassicChampionData,
  type ClassicChampionSkillsMini,
  type ClassicSummonerSpell,
  type ClassicPerk,
  type ClassicRuneBuildFile,
  type ClassicItem,
  type ClassicItemBuildFile,
} from "@ugg/shared/hooks/classic";
import { SkillTooltipContent, formatCooldown, SKILL_KEY_LABEL } from "./lib/skill-tooltip";
import { ClassicItemIcon } from "./ClassicItemPrimitives";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

const RUNE_CATEGORIES: Array<{ key: keyof ClassicRuneBuildFile["runes"]; label: string; color: string }> = [
  { key: "kQuintessence", label: "Quintessence", color: "#F9BE90" },
  { key: "kMark", label: "Mark", color: "#B55A40" },
  { key: "kSeal", label: "Seal", color: "#FFE887" },
  { key: "kGlyph", label: "Glyph", color: "#71AFD5" },
];

// ─── Skill tooltip rendering ────────────────────────────────────────────────

// Color map for the custom markup tags used in the champion JSON's
// dynamicDescription (e.g. <magicDamage>…</magicDamage>). Unknown tags render
// with inherited color. Tuned to read like the in-client tooltip colors.

export function ClassicRecommendedPanel(props: {
  name: string;
  roleLabel: string;
  spellIds: number[];
  spellsById: Record<number, ClassicSummonerSpell>;
  runeBuild?: ClassicRuneBuildFile;
  perksById: Record<number, ClassicPerk>;
  autoImportButton?: ReactNode;
  // Skill Priority lives inside the summoner-spells cell (its own dedicated
  // module below was removed), resolved from the compact "mini" priority feed.
  championKey?: string;
  champion?: ClassicChampionData;
  skillsMini?: ClassicChampionSkillsMini;
  // Item build now lives at the bottom of the right column (replacing the CTA).
  itemBuild?: ClassicItemBuildFile;
  itemsById: Record<number, ClassicItem>;
  itemsMini?: Record<string, string>;
}) {
  const { name, roleLabel, spellIds, spellsById, runeBuild, perksById, autoImportButton } = props;
  const { championKey, champion, skillsMini } = props;
  const { itemBuild, itemsById, itemsMini } = props;

  const priorityKeys = resolveSkillPriorityMini(skillsMini);

  const showWarningTip = false;
  const warningTip = <div className="max-w-[300px] text-[13px]">Classic builds are a work in progress</div>;

  // Item build for the new items module (bottom of the right column). Starting
  // Items = the opening buy (duplicate ids → a stacked consumable, shown with a
  // quantity badge); Final Build = the finished ≤6-item build. Situational Items
  // = matchup-dependent swaps; falls back to placeholder slots when the feed is empty.
  const starter = asArray(itemBuild?.items?.starter);
  const endgame = asArray(itemBuild?.items?.endgame);
  const situational = asArray(itemBuild?.items?.situational);
  const starterCounts = new Map<number, number>();
  starter.forEach((id) => starterCounts.set(id, (starterCounts.get(id) || 0) + 1));
  const starterUnique = [...starterCounts.keys()];
  const emptyItem = <div className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />;
  const renderItem = (id: number, quantity?: number) => (
    <ClassicItemIcon itemId={id} item={itemsById[id]} name={itemsMini?.[String(id)]} quantity={quantity} />
  );

  return (
    <div className="rune-spell">
      <div className="flex h-[42px]">
        <div className="flex-1">
          {/* Static "Recommended" tab, styled like BuildTabs' active default tab.
              The rune/item icons were removed with the multibuild filter. */}
          <div
            className="inline-flex items-center px-[12px] py-[10px] h-[42px] rounded-t-[3px] shadow-[inset_0px_2px_0px_#ff9b00]"
            style={{ backgroundColor: "#11112A" }}
          >
            <strong className="font-['Inter'] text-[14px] leading-[20px] text-white font-bold whitespace-nowrap">
              Recommended
            </strong>
          </div>
        </div>
        {/* Reuses the site's AppDownloadButton, injected from the web shell. */}
        {autoImportButton && (
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
            {autoImportButton}
          </MediaQuery>
        )}
        {showWarningTip && (
          <TooltipContainer tooltipInfo={warningTip} customStyle={{ maxWidth: "400px" }} tooltipHoverable>
            <div className="flex items-center justify-center ml-[6px] w-[34px] h-[34px] bg-purple-400 rounded-[3px]">
              <Warning className="flex-none w-[18px] h-[18px]" />
            </div>
          </TooltipContainer>
        )}
      </div>

      {/* Module body — custom flex layout (the legacy `.grid-1` grid was replaced
          because its `min-content 1fr` rows grew the summoner cell when we forced
          the module height). Kept the `.content-section content-section_unset`
          wrapper only so the nested `.content-section_header` blue-bar styling
          still applies. Two columns: left runes (#11112A, flex-1) and a fixed
          443px right column (#191937) split into a ~110px summoner/skill section
          over a flex-1 items section. minHeight hits the figma 417px. */}
      <div className="content-section content-section_unset flex max-sm:flex-col" style={{ minHeight: 417 }}>
        {/* LEFT — runes (#11112A) */}
        <div
          className="flex-1 min-w-0 flex flex-col p-[18px] max-sm:p-[12px]"
          style={{ backgroundColor: "#11112A", borderRight: "1px solid #070720" }}
        >
          <div className="content-section_header">
            <div className="main-header">
              <span style={{ marginRight: 12 }}>{`${name} Runes`}</span>
              <MediaQuery min="TABLET" max="DESKTOP_LARGE">
                <span className="content-section_blurb truncate" style={{ direction: "rtl" }}>
                  {`${name} ${roleLabel} Build`}
                </span>
              </MediaQuery>
            </div>
          </div>
          <ClassicRunesCompact runeBuild={runeBuild} perksById={perksById} className="flex-1" />
        </div>

        {/* RIGHT — fixed 443px column (#191937): summoner/skill over items.
            maxWidth caps it so it collapses instead of overflowing on mobile. */}
        <div className="flex-none flex flex-col" style={{ width: 443, maxWidth: "100%", backgroundColor: "#191937" }}>
          {/* Top: Summoner Spells (170px) | 1px #070720 divider | Skill Priority
              (fills the rest, ~272px). Padding per column: 18px top → text, 18px
              header→icons (header margin-bottom), 12px between summoner icons, 8px
              icon↔arrow in skill. 18px bottom padding leaves 18px under the summoner
              icons; the skill keybind badge overhangs ~6px so ~12px shows under it. */}
          <div className="flex flex-none">
            {/* Summoner Spells — 170px wide */}
            <div className="flex-none p-[18px] max-sm:p-[12px]" style={{ width: 170 }}>
              <div className="content-section_header">
                <div>Summoner Spells</div>
              </div>
              <div className="flex gap-[12px]">
                {spellIds.length > 0
                  ? spellIds
                      .slice(0, 2)
                      .map((id, i) => <ClassicSummonerSpellIcon key={`${id}-${i}`} spellId={id} spell={spellsById[id]} />)
                  : [0, 1].map((i) => (
                      <div key={i} className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />
                    ))}
              </div>
            </div>

            {/* 1px #070720 divider, full section height */}
            <div className="flex-none self-stretch w-px" style={{ backgroundColor: "#070720" }} />

            {/* Skill Priority — fills the rest (~272px) */}
            <div className="flex-1 min-w-0 p-[18px] max-sm:p-[12px]">
              <div className="content-section_header">
                <div>Skill Priority</div>
              </div>
              <div className="flex flex-wrap items-center gap-y-[6px]">
                {priorityKeys && priorityKeys.length > 0
                  ? priorityKeys.map((key, index) => (
                      <Fragment key={`${key}-${index}`}>
                        <ClassicSkillIcon
                          championKey={championKey}
                          champion={champion}
                          skillKey={key as "q" | "w" | "e" | "r"}
                          className="rounded-[3px]"
                        />
                        {index !== priorityKeys.length - 1 && <ArrowRight className="mx-[8px] w-[12px] h-[12px] flex-none" />}
                      </Fragment>
                    ))
                  : [0, 1, 2].map((i) => (
                      <Fragment key={i}>
                        <div className="flex-none rounded-[3px] bg-[#25254b]/50" style={{ width: 36, height: 36 }} />
                        {i !== 2 && <ArrowRight className="mx-[8px] w-[12px] h-[12px] flex-none" />}
                      </Fragment>
                    ))}
              </div>
            </div>
          </div>

          {/* Horizontal #070720 groove between the top section and the items. */}
          <div className="flex-none" style={{ height: 1, backgroundColor: "#070720" }} />

          {/* Bottom: items (fills the rest). Starting / Final Build / Situational. */}
          <div className="flex-1 p-[18px] max-sm:p-[12px]">
            <div className="content-section_header">
              <div>Starting Items</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {starterUnique.length > 0
                ? starterUnique.map((id) => <Fragment key={id}>{renderItem(id, starterCounts.get(id))}</Fragment>)
                : [0, 1].map((i) => <Fragment key={i}>{emptyItem}</Fragment>)}
            </div>

            <div className="content-section_header mt-[24px]">
              <div>Final Build</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {endgame.length > 0
                ? endgame.slice(0, 6).map((id, i) => <Fragment key={`${id}-${i}`}>{renderItem(id)}</Fragment>)
                : [0, 1, 2, 3, 4, 5].map((i) => <Fragment key={i}>{emptyItem}</Fragment>)}
            </div>

            {/* Situational Items — matchup-dependent swaps; placeholders if none. */}
            <div className="content-section_header mt-[24px]">
              <div>Situational Items</div>
            </div>
            <div className="flex flex-wrap gap-[12px]">
              {situational.length > 0
                ? situational.slice(0, 3).map((id, i) => <Fragment key={`${id}-${i}`}>{renderItem(id)}</Fragment>)
                : [0, 1, 2].map((i) => <Fragment key={i}>{emptyItem}</Fragment>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact runes for the Recommended panel. SHORT-TERM layout: with sparse data
// the old four-column grid left most of the panel empty and read badly, so each
// rune is now its OWN card (Rune Summary content — quantity + icon + full title +
// effect) with a category-colored left accent, stacked in a SINGLE column of
// uniform full-width cards that stretch (flex-1) to fill the panel's height and
// its full width. Runes are flattened in canonical category order (Quintessence →
// Mark → Seal → Glyph). The #1C1C3C bordered panel and its caller-driven fill
// height (className, e.g. flex-1) are kept. `font-['Inter']` so the card text
// matches the rest of the redesigned modules.
function ClassicRunesCompact(props: {
  runeBuild?: ClassicRuneBuildFile;
  perksById: Record<number, ClassicPerk>;
  className?: string;
}) {
  const { runeBuild, perksById, className } = props;
  if (!runeBuild?.runes) return null;

  // Flatten every category's runes into individual cards, tagging each with its
  // category color for the accent bar (keeps the color-coding without headers).
  const cards = RUNE_CATEGORIES.flatMap((cat) =>
    asArray(runeBuild.runes[cat.key]).map((entry, i) => {
      const [perkId, count] = Array.isArray(entry) ? entry : [0, 0];
      return { key: `${cat.key}-${perkId}-${i}`, perkId, count, color: cat.color };
    }),
  );

  return (
    <div
      className={classNames("flex flex-col overflow-hidden rounded-[3px]", className)}
      // Colors + min-height set inline (not Tailwind arbitrary classes) so they
      // apply reliably; minHeight keeps the module at the figma ~417px.
      style={{ backgroundColor: "#1C1C3C", border: "1px solid #383864", minHeight: 341 }}
    >
      {/* Single column of full-width cards that share the panel height equally
          (each flex-1) — uniform width AND height, stretched to both edges. */}
      <div className="flex flex-1 flex-col gap-[12px] p-[14px]">
        {cards.map(({ key, perkId, count, color }) => {
          const perk = perksById[perkId];
          return (
            <div
              key={key}
              // flex-1 → equal-height cards filling the column; min-h keeps the
              // 36px icon from clipping if the list ever gets long enough that an
              // equal share would be shorter than the icon.
              className="relative flex flex-1 items-center gap-[10px] overflow-hidden rounded-[3px] bg-[#11112a] pl-[16px] pr-[12px] min-h-[52px] font-['Inter']"
            >
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: color }} />
              <div className="w-[24px] flex-none text-center text-[12px] font-bold text-white">{count}x</div>
              {perk?.iconPath ? (
                <img
                  src={getClassicRuneIconUrl(perk.iconPath)}
                  alt={perk.title}
                  loading="lazy"
                  className="flex-none w-[36px] h-[36px]"
                />
              ) : (
                <div className="flex-none w-[36px] h-[36px] rounded-full bg-[#25254b]/50" />
              )}
              <div className="min-w-0">
                <div className="text-[12px] font-bold text-white">{perk?.title ?? `Rune ${perkId}`}</div>
                <div className="text-[11px] leading-[15px] text-lavender-50">{perk?.tooltip}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A single recommended summoner spell: icon + hover tooltip (name, cooldown,
// description) resolved from the spell manifest. Renders the bare icon if the
// manifest hasn't resolved the id yet.
function ClassicSummonerSpellIcon(props: { spellId: number; spell?: ClassicSummonerSpell }) {
  const { spellId, spell } = props;
  const icon = (
    <img
      src={getClassicSpellIconUrl(spellId)}
      alt={spell?.name || `Spell ${spellId}`}
      className="w-[36px] h-[36px] rounded-[3px]"
    />
  );
  if (!spell) return icon;

  return (
    <TooltipContainer
      tooltipInfo={
        <div className="max-w-[280px] text-[13px] leading-[18px]">
          <div className="font-bold text-accent-blue-400">{spell.name}</div>
          {spell.cooldown ? <div className="mt-[2px] text-[12px] text-lavender-50">Cooldown: {spell.cooldown}s</div> : null}
          <div className="mt-[6px] text-white">{spell.description}</div>
        </div>
      }
    >
      {icon}
    </TooltipContainer>
  );
}

// ─── Masteries (tabbed, full width) ───────────────────────────────────────────

// Per-tree accent color — drives the header underline, the selected-node borders,
// and the active connectors (figma: Offense red, Defense blue, Utility green).

// Ability keys that can appear in a priority entry. The mini feed uses uppercase
// ("Q"); we lowercase to match SKILL_KEY_LABEL and the icon's skillKey prop.
const VALID_SKILL_PRIORITY_KEYS = new Set(["q", "w", "e", "r"]);

// Resolve the skill-priority order from the mini feed — a flat array of ability
// keys, e.g. ["Q","W","E"]. Defensive against a malformed payload: a non-array,
// non-string entries, or unknown keys are dropped. Returns the lowercased keys,
// or an empty array (→ blank placeholder slots) when there's no usable data.
function resolveSkillPriorityMini(mini: ClassicChampionSkillsMini | undefined): string[] {
  return asArray(mini)
    .map((k) => asText(k).toLowerCase())
    .filter((k) => VALID_SKILL_PRIORITY_KEYS.has(k));
}

// A single champion-skill icon (P/Q/W/E/R) with the shared hover tooltip (name,
// cooldown, description). Used by the Skill Priority row in the Recommended panel.
function ClassicSkillIcon(props: {
  championKey?: string;
  champion?: ClassicChampionData;
  skillKey: "p" | "q" | "w" | "e" | "r";
  className: string;
}) {
  const { championKey, champion, skillKey, className } = props;
  const isPassive = skillKey === "p";
  const spell = asArray(champion?.spells).find((s) => s.spellKey === skillKey);
  const tipName = isPassive ? champion?.passive?.name : spell?.name;
  return (
    <TooltipContainer
      disableTooltip={!champion}
      tooltipInfo={
        <SkillTooltipContent
          name={tipName || SKILL_KEY_LABEL[skillKey]}
          cooldown={formatCooldown(spell?.cooldownCoefficients)}
          description={isPassive ? champion?.passive?.description : spell?.dynamicDescription}
          isPassive={isPassive}
        />
      }
    >
      <div className="relative flex-none">
        {championKey && (
          <img
            src={getClassicChampionSkillImgUrl(championKey, skillKey)}
            alt={tipName || SKILL_KEY_LABEL[skillKey]}
            className={className}
            style={{ width: 36, height: 36 }}
          />
        )}
        {/* Keybind badge — same overlay the champion header uses, so the Skill
            Priority icons read identically to the top module. */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
          {SKILL_KEY_LABEL[skillKey]}
        </div>
      </div>
    </TooltipContainer>
  );
}

// ─── Items ────────────────────────────────────────────────────────────────
//
// Colors for the item-description markup (attention = stat numbers, jadeUnique =
// gold labels, plus the shared value-color tags). <mainText>/<stats> are pure
// wrappers (no style) so their text renders default. Non-color tags carry extra
// style (bold/italic/size).
